"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "m4b_cookie_consent";

export type CookieConsentValue = "all" | "essential";

function readConsent(): CookieConsentValue | null {
  if (typeof window === "undefined") return null;
  const value = window.localStorage.getItem(STORAGE_KEY);
  if (value === "all" || value === "essential") return value;
  return null;
}

export function getCookieConsent(): CookieConsentValue | null {
  return readConsent();
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(readConsent() === null);
  }, []);

  const save = (value: CookieConsentValue) => {
    window.localStorage.setItem(STORAGE_KEY, value);
    window.dispatchEvent(
      new CustomEvent("m4b:cookie-consent", { detail: value })
    );
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      className="fixed inset-x-0 bottom-0 z-[100] p-4 md:p-6"
    >
      <div className="mx-auto max-w-3xl rounded-xl border border-border bg-background/95 backdrop-blur-md shadow-2xl p-5 md:p-6">
        <h2
          id="cookie-consent-title"
          className="text-base font-semibold text-foreground mb-2"
        >
          Cookies y privacidad
        </h2>
        <p
          id="cookie-consent-desc"
          className="text-sm text-muted-foreground leading-relaxed mb-4"
        >
          Utilizamos cookies técnicas necesarias para el funcionamiento del
          sitio y, con tu consentimiento, cookies de análisis para mejorar la
          experiencia. Puedes aceptar todas o continuar solo con las esenciales.
          Más información en nuestra{" "}
          <Link href="/cookies" className="text-primary underline underline-offset-2">
            Política de cookies
          </Link>{" "}
          y{" "}
          <Link href="/privacidad" className="text-primary underline underline-offset-2">
            Privacidad
          </Link>
          .
        </p>
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => save("essential")}
            className="sm:order-1"
          >
            Solo esenciales
          </Button>
          <Button onClick={() => save("all")} className="sm:order-2">
            Aceptar todas
          </Button>
        </div>
      </div>
    </div>
  );
}
